import React from 'react';
import Router from 'next/router';
import { proxy, useSnapshot } from 'valtio';
import { createContainer } from '../utils/unstated';

export const SecondaryModalType = {
  RECONNECT_WALLET: 'RECONNECT_WALLET',
  SWITCH_CHAIN: 'SWITCH_CHAIN',
  SWITCH_CHAIN_IN_WALLET: 'SWITCH_CHAIN_IN_WALLET',
  EDITION_VIEW_COVER: 'EDITION_VIEW_COVER',
} as const;

export type SecondaryModalType = (typeof SecondaryModalType)[keyof typeof SecondaryModalType];

export type ModalContentState = {
  body: React.ReactNode;
};

const secondaryGlobalModalState = proxy({
  activeModal: null as SecondaryModalType | null,
  openModal: (newModalType: SecondaryModalType) => {
    secondaryGlobalModalState.activeModal = newModalType;
  },
  onCancelModal: null as (() => void) | null,
  setOnCancelModal: (onCancelModal: () => void | null) => {
    secondaryGlobalModalState.setOnCancelModal = onCancelModal;
  },
  closeModal: () => {
    secondaryGlobalModalState.activeModal = null;
    secondaryGlobalModalState.onCancelModal = null;
  },
  closeModalByType: (modalType: SecondaryModalType) => {
    if (secondaryGlobalModalState.activeModal === modalType) {
      secondaryGlobalModalState.closeModal();
    }
  },
  closeCancelModal: () => {
    if (secondaryGlobalModalState.onCancelModal !== null) {
      secondaryGlobalModalState.onCancelModal();
    }
    secondaryGlobalModalState.closeModal();
  },
});

if (typeof window !== 'undefined') {
  // If leaving the page, close the modal
  Router.events.on('routeChangeStart', (destination: string) => {
    /* need this extra guard because useRouterRefresh triggers routeChangeStart
     * for the route we're already on */
    if (destination !== Router.asPath) {
      secondaryGlobalModalState.closeModal();
    }
  });
}

export const SecondaryModalContext = createContainer(function useModal() {
  const [modalContent, setModalContent] = React.useState<ModalContentState | null>(null);

  const openModal = React.useCallback(
    (modalType: SecondaryModalType, modalContent: ModalContentState | null) => {
      setModalContent(modalContent);

      secondaryGlobalModalState.activeModal = modalType;
    },
    [setModalContent],
  );

  const { activeModal, onCancelModal } = useSnapshot(secondaryGlobalModalState);

  return {
    activeModal,
    onCancelModal,
    modalContent,
    openModal,
    closeCancelModal: secondaryGlobalModalState.closeCancelModal,
    setOnCancelModal: secondaryGlobalModalState.setOnCancelModal,
    closeModal: secondaryGlobalModalState.closeModal,
    closeModalByType: secondaryGlobalModalState.closeModalByType,
  };
});

export const useSecondaryModal = () => SecondaryModalContext.useContainer();
